import html2canvasPro from "html2canvas-pro"
import { jsPDF } from "jspdf"

export function getPdfFileName(title: string) {
	return `Zonhub - ${title}.pdf`
}

type Compression = "NONE" | "FAST" | "MEDIUM" | "SLOW"

interface GeneratePDFOptions {
	filename: string
	overrideWidth?: number
	compression?: Compression
}

const A4_WIDTH_PT = 595.28
const A4_HEIGHT_PT = 841.89

/**
 * Collect every CSS rule applied to the current document and return it as a
 * single string. In production Vite emits CSS as external <link> stylesheets;
 * html2canvas-pro's cloned iframe doesn't reliably wait for those to load
 * before rendering, so we inline the CSS into the clone instead.
 *
 * Same-origin sheets expose `cssRules`; cross-origin sheets throw a
 * SecurityError on access and are fetched as text as a fallback (the network
 * call is cached by the browser/SW so it's effectively free).
 */
async function collectDocumentCss(): Promise<string> {
	const parts: string[] = []
	const fetchPromises: Promise<string>[] = []

	for (const sheet of Array.from(document.styleSheets)) {
		try {
			const rules = sheet.cssRules
			if (rules) {
				const ruleParts: string[] = []
				for (const rule of Array.from(rules)) {
					ruleParts.push(rule.cssText)
				}
				parts.push(ruleParts.join(""))
				continue
			}
		} catch {
			// Cross-origin sheet — fall through to fetch by href.
		}

		if (sheet.href) {
			fetchPromises.push(
				fetch(sheet.href, { credentials: "omit" })
					.then((res) => (res.ok ? res.text() : ""))
					.catch(() => ""),
			)
		}
	}

	const fetched = await Promise.all(fetchPromises)
	return [...parts, ...fetched].join("\n")
}

export async function generatePDF(
	element: HTMLElement,
	{ filename, overrideWidth, compression = "NONE" }: GeneratePDFOptions,
) {
	const sourceWidthPx = overrideWidth ?? element.getBoundingClientRect().width
	// PDF unit ratio: 1 source-pixel maps to (A4_WIDTH_PT / sourceWidthPx) PDF points.
	const ptPerPx = A4_WIDTH_PT / sourceWidthPx
	// Each A4 page holds this many source pixels of height.
	const pageHeightPx = A4_HEIGHT_PT / ptPerPx

	const inlinedCss = await collectDocumentCss()

	const canvas = await html2canvasPro(element, {
		backgroundColor: "#ffffff",
		useCORS: true,
		width: overrideWidth,
		windowWidth: overrideWidth,
		scale: window.devicePixelRatio || 1,
		onclone: (clonedDoc, clonedRoot) => {
			// Inline every stylesheet from the parent document into a single
			// <style> in the clone. Without this, production renders unstyled
			// because the cloned <link> tags aren't guaranteed to apply before
			// html2canvas-pro starts rasterizing.
			const head =
				clonedDoc.head ?? clonedDoc.getElementsByTagName("head").item(0)
			if (head) {
				const styleEl = clonedDoc.createElement("style")
				styleEl.setAttribute("data-html2canvas-inlined", "true")
				styleEl.textContent = inlinedCss
				head.appendChild(styleEl)
			}

			// Strip box-shadows in the clone: html2canvas-pro renders some
			// shadow alpha values as a multiply over the element's own
			// background, which desaturates solid fills. The shadow only
			// exists to lift the bar in the web view; the PDF doesn't need it.
			clonedRoot.querySelectorAll<HTMLElement>("*").forEach((el) => {
				if (el.style.boxShadow || getComputedStyle(el).boxShadow) {
					el.style.boxShadow = "none"
				}
			})
		},
	})

	const scale = canvas.width / sourceWidthPx
	const slicePxOnCanvas = pageHeightPx * scale
	const totalPages = Math.max(1, Math.ceil(canvas.height / slicePxOnCanvas))

	const pdf = new jsPDF({ orientation: "p", unit: "pt", format: "a4" })

	const pageCanvas = document.createElement("canvas")
	const pageCtx = pageCanvas.getContext("2d")
	if (!pageCtx) {
		throw new Error("Failed to get 2D context for PDF page canvas")
	}

	for (let page = 0; page < totalPages; page++) {
		const sourceY = page * slicePxOnCanvas
		const sliceHeight = Math.min(slicePxOnCanvas, canvas.height - sourceY)

		pageCanvas.width = canvas.width
		pageCanvas.height = sliceHeight
		pageCtx.fillStyle = "#ffffff"
		pageCtx.fillRect(0, 0, pageCanvas.width, pageCanvas.height)
		pageCtx.drawImage(
			canvas,
			0,
			sourceY,
			canvas.width,
			sliceHeight,
			0,
			0,
			canvas.width,
			sliceHeight,
		)

		// Final page can be shorter than A4; render at proportional height so
		// content keeps the same scale as full pages.
		const pageHeightPt = (sliceHeight / scale) * ptPerPx

		if (page > 0) pdf.addPage()
		pdf.addImage(
			pageCanvas.toDataURL("image/png"),
			"PNG",
			0,
			0,
			A4_WIDTH_PT,
			pageHeightPt,
			undefined,
			compression,
		)
	}

	pdf.save(filename)
}
